// RdPicker.tsx — eigener kvp-rd/cal-rd-Datepicker (ersetzt den Fluent
// `DatePicker` im Kalender-Neubau). Trigger-Button mit formatiertem Datum
// (dt. Locale) + Popover (RdPopover) mit 6×7-Monatsraster (Logik wie MiniMonth).
// Kein neuer Lib-Dependency, konsistente REFINED-Optik. (SP1, festgenagelt.)

import React, { useMemo, useState } from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)
import { RdPopover } from "../redesign/table/RdPopover";
import { REFINED } from "../risikoanalyseStep/tokens";
import { isoKalenderwoche } from "./kalenderwoche";

/** Mo–So-Kürzel (deutsche Woche beginnt montags). */
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/**
 * Breite des Popovers um das Kalenderblatt. Seit T-194 (05.10.2026) steht
 * links die Spalte „KW"; damit die Tage so groß bleiben wie vorher, ist das
 * Blatt von 252 auf 280 px gewachsen. Alle Popover, die `RdMonthGrid` tragen,
 * nehmen diesen Wert.
 */
export const RD_MONTH_GRID_BREITE = 280;

/** Spalten des Blatts: schmale KW-Spalte, dann Mo–So. */
const SPALTEN = "22px repeat(7, 1fr)";

interface RdDatePickerProps {
    value: Date;
    onChange: (d: Date) => void;
    /** Optionale Breite des Triggers; Popover bleibt fix breit. */
    minWidth?: number;
}

const RdDatePicker: React.FC<RdDatePickerProps> = ({ value, onChange, minWidth = 168 }) => {
    return (
        <RdPopover
            align="start"
            width={RD_MONTH_GRID_BREITE}
            trigger={({ open, toggle, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    onClick={toggle}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth,
                        minHeight: 36,
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: `1px solid ${REFINED.line}`,
                        background: REFINED.white,
                        color: REFINED.ink,
                        font: `500 14px ${REFINED.fontUI}`,
                        cursor: "pointer",
                        boxShadow: open ? REFINED.focusRing : undefined,
                    }}
                >
                    <span style={{ fontSize: 13, color: REFINED.aub }}>▾</span>
                    <span style={{ textTransform: "capitalize" }}>
                        {moment(value).format("D. MMMM YYYY")}
                    </span>
                </button>
            )}
        >
            {(close) => (
                <RdMonthGrid
                    value={value}
                    onPick={(d) => {
                        onChange(d);
                        close();
                    }}
                />
            )}
        </RdPopover>
    );
};

/**
 * 6×7-Monatsraster mit eigener Monatsnavigation (Vorbild: MiniMonth).
 * Exportiert, damit auch andere Popover-Trigger (z. B. die TWV/Frist-Zeile im
 * Vorgang-Detail, SP7) denselben kvp-rd/cal-rd-Datepicker teilen.
 *
 * Links vor jeder Woche steht grau ihre ISO-Kalenderwoche (T-194).
 */
export const RdMonthGrid: React.FC<{ value: Date; onPick: (d: Date) => void }> = ({ value, onPick }) => {
    const [cursor, setCursor] = useState<moment.Moment>(() => moment(value).startOf("month"));

    /** Sechs Wochen à sieben Tage, jede Zeile beginnt montags. */
    const weeks = useMemo(() => {
        const gridStart = cursor.clone().startOf("month").startOf("isoWeek");
        const rows: moment.Moment[][] = [];
        for (let w = 0; w < 6; w++) {
            const row: moment.Moment[] = [];
            for (let i = 0; i < 7; i++) row.push(gridStart.clone().add(w * 7 + i, "days"));
            rows.push(row);
        }
        return rows;
    }, [cursor]);

    const todayKey = moment().format("YYYY-MM-DD");
    const valueKey = moment(value).format("YYYY-MM-DD");
    const monthKey = cursor.format("YYYY-MM");

    return (
        <div
            className="cal-rd"
            style={{
                userSelect: "none",
                background: REFINED.white,
                border: `1px solid ${REFINED.line}`,
                borderRadius: 10,
                boxShadow: REFINED.cardShadow,
                padding: 10,
                maxWidth: "100%",
                font: `400 12px ${REFINED.fontUI}`,
            }}
        >
            {/* Kopf: Monat/Jahr + Blätterpfeile */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                }}
            >
                <button
                    type="button"
                    aria-label="Vorheriger Monat"
                    onClick={() => setCursor((c) => c.clone().subtract(1, "month"))}
                    style={navBtnStyle}
                >
                    ◄
                </button>
                <span style={{ fontWeight: 600, color: REFINED.ink, textTransform: "capitalize" }}>
                    {cursor.format("MMMM YYYY")}
                </span>
                <button
                    type="button"
                    aria-label="Nächster Monat"
                    onClick={() => setCursor((c) => c.clone().add(1, "month"))}
                    style={navBtnStyle}
                >
                    ►
                </button>
            </div>

            {/* Wochentags-Kopf */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: SPALTEN,
                    columnGap: 1,
                    textAlign: "center",
                    color: REFINED.ink3,
                    fontSize: 11,
                    marginBottom: 2,
                }}
            >
                <div data-kw-kopf title="Kalenderwoche" style={kwStyle}>
                    KW
                </div>
                {WEEKDAYS.map((d) => (
                    <div key={d} style={{ padding: "2px 0" }}>
                        {d}
                    </div>
                ))}
            </div>

            {/* Tage-Raster — je Woche zuerst die KW, dann Mo–So */}
            <div style={{ display: "grid", gridTemplateColumns: SPALTEN, gap: 1 }}>
                {weeks.flatMap((week) => {
                    const kw = isoKalenderwoche(week[0].toDate());
                    return [
                        <div
                            key={`kw-${week[0].format("YYYY-MM-DD")}`}
                            data-kw
                            title={`Kalenderwoche ${kw.woche}`}
                            style={{ ...kwStyle, display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                            {kw.woche}
                        </div>,
                        ...week.map((day) => {
                            const key = day.format("YYYY-MM-DD");
                            const inMonth = day.format("YYYY-MM") === monthKey;
                            const isToday = key === todayKey;
                            const isSelected = key === valueKey;
                            const isWeekend = day.isoWeekday() >= 6;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    data-tag
                                    onClick={() => onPick(day.toDate())}
                                    title={day.format("dddd, D. MMMM YYYY")}
                                    style={{
                                        aspectRatio: "1 / 1",
                                        minHeight: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: "none",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        font: "inherit",
                                        fontSize: 12,
                                        lineHeight: 1,
                                        fontWeight: isSelected || isToday ? 700 : 400,
                                        background: isSelected
                                            ? REFINED.aub
                                            : isToday
                                              ? REFINED.aub100
                                              : "transparent",
                                        color: isSelected
                                            ? REFINED.white
                                            : inMonth
                                              ? isWeekend
                                                  ? REFINED.ink3
                                                  : REFINED.ink
                                              : "#ccc",
                                    }}
                                    onMouseOver={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = REFINED.aub50;
                                    }}
                                    onMouseOut={(e) => {
                                        if (!isSelected)
                                            e.currentTarget.style.background = isToday
                                                ? REFINED.aub100
                                                : "transparent";
                                    }}
                                >
                                    {day.date()}
                                </button>
                            );
                        }),
                    ];
                })}
            </div>
        </div>
    );
};

/** Die KW-Spalte: klein und grau, damit sie neben den Tagen zurücktritt. */
const kwStyle: React.CSSProperties = {
    padding: "2px 0",
    fontSize: 10,
    color: REFINED.ink3,
    opacity: 0.8,
    fontVariantNumeric: "tabular-nums",
    borderRight: `1px solid ${REFINED.line}`,
};

const navBtnStyle: React.CSSProperties = {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 6,
    background: REFINED.white,
    color: REFINED.ink2,
    cursor: "pointer",
    fontSize: 10,
    padding: 0,
    flexShrink: 0,
};

export default RdDatePicker;
